import { getLocalStorageInstance } from '~/helpers/storage.ts';

export const MANAGER_VIEWED_DOCUMENTS_STORAGE_TTL_MS = 60 * 24 * 60 * 60 * 1000;
export const MANAGER_VIEWED_DOCUMENTS_UNKNOWN_MANAGER = 'unknown-manager';

export type TManagerViewedDocumentsById = Record<string, boolean>;

type TManagerViewedDocumentsStoragePayload = {
  viewedDocuments: Record<string, string>;
  expiresAt: string;
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const normalizeStorageSegment = (value?: string | null): string => {
  const normalizedValue = String(value ?? '').trim().toLowerCase();

  return encodeURIComponent(normalizedValue || MANAGER_VIEWED_DOCUMENTS_UNKNOWN_MANAGER);
};

const buildViewedDocumentsMap = (
  viewedDocuments: Record<string, string>
): TManagerViewedDocumentsById =>
  Object.keys(viewedDocuments).reduce<TManagerViewedDocumentsById>(
    (accumulator, documentId) => ({
      ...accumulator,
      [documentId]: true,
    }),
    {}
  );

const isValidViewedDocumentsPayload = (
  value: unknown
): value is TManagerViewedDocumentsStoragePayload => {
  if (!isObjectRecord(value)) {
    return false;
  }

  const { expiresAt, viewedDocuments } = value;

  return (
    typeof expiresAt === 'string' &&
    isObjectRecord(viewedDocuments) &&
    Object.values(viewedDocuments).every((viewedAt) => typeof viewedAt === 'string')
  );
};

const getValidStoredPayload = ({
  manager,
  applicationId,
  nowMs,
}: {
  manager?: string | null;
  applicationId: string;
  nowMs: number;
}): TManagerViewedDocumentsStoragePayload | null => {
  const localStorageInstance = getLocalStorageInstance();

  if (!localStorageInstance) {
    return null;
  }

  const storageKey = getManagerViewedDocumentsStorageKey({ manager, applicationId });
  const rawValue = localStorageInstance.getItem(storageKey);

  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue);

    if (!isValidViewedDocumentsPayload(parsedValue)) {
      localStorageInstance.removeItem(storageKey);
      return null;
    }

    const expiresAtMs = Date.parse(parsedValue.expiresAt);

    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= nowMs) {
      localStorageInstance.removeItem(storageKey);
      return null;
    }

    return parsedValue;
  } catch {
    localStorageInstance.removeItem(storageKey);
    return null;
  }
};

export const getManagerViewedDocumentsStorageKey = ({
  manager,
  applicationId,
}: {
  manager?: string | null;
  applicationId: string;
}): string =>
  `covenants:manager:reviewed-documents:v1:${normalizeStorageSegment(manager)}:${applicationId}`;

export const getManagerViewedDocuments = ({
  manager,
  applicationId,
  nowMs = Date.now(),
}: {
  manager?: string | null;
  applicationId?: string | null;
  nowMs?: number;
}): TManagerViewedDocumentsById => {
  const localStorageInstance = getLocalStorageInstance();

  if (!localStorageInstance || !applicationId) {
    return {};
  }

  return buildViewedDocumentsMap(
    getValidStoredPayload({ manager, applicationId, nowMs })?.viewedDocuments ?? {}
  );
};

export const markManagerDocumentAsViewed = ({
  manager,
  applicationId,
  documentFileId,
  nowMs = Date.now(),
}: {
  manager?: string | null;
  applicationId?: string | null;
  documentFileId?: string | number | null;
  nowMs?: number;
}): TManagerViewedDocumentsById => {
  const normalizedDocumentFileId = String(documentFileId ?? '').trim();
  const localStorageInstance = getLocalStorageInstance();

  if (!applicationId || !normalizedDocumentFileId) {
    return {};
  }

  const storedPayload = getValidStoredPayload({
    manager,
    applicationId,
    nowMs,
  });
  const currentViewedDocuments = buildViewedDocumentsMap(storedPayload?.viewedDocuments ?? {});
  const nextViewedDocuments = {
    ...currentViewedDocuments,
    [normalizedDocumentFileId]: true,
  };

  if (!localStorageInstance) {
    return nextViewedDocuments;
  }

  const storageKey = getManagerViewedDocumentsStorageKey({ manager, applicationId });
  const viewedAt = new Date(nowMs).toISOString();
  const expiresAt = new Date(nowMs + MANAGER_VIEWED_DOCUMENTS_STORAGE_TTL_MS).toISOString();
  const payload: TManagerViewedDocumentsStoragePayload = {
    viewedDocuments: {
      ...(storedPayload?.viewedDocuments ?? {}),
      [normalizedDocumentFileId]: viewedAt,
    },
    expiresAt,
  };

  try {
    localStorageInstance.setItem(storageKey, JSON.stringify(payload));
  } catch {
    return nextViewedDocuments;
  }

  return nextViewedDocuments;
};
